// src/components/ui/NuevoProducto.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE   = resolveApiBase();
const NODISP     = `${API_BASE}/public/nodisponible.png`;
const LIST_ROUTE = '/admin/productos';

export default function NuevoProducto() {
  const navigate = useNavigate();
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [imagenFile, setImagenFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const tickRef = useRef(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const formik = useFormik({
    initialValues: { nombre:'', marca:'', presentacion:'', precio:'', stock:'' },
    validationSchema: Yup.object({
      nombre: Yup.string().required('Nombre del producto es obligatorio'),
      marca: Yup.string().required('Nombre de la marca es obligatorio'),
      presentacion: Yup.string().required('Nombre de la presentación es obligatorio'),
      precio: Yup.number().typeError('Número inválido').required('Precio del producto es obligatorio').positive('Debe ser positivo'),
      stock: Yup.number().typeError('Número inválido').required('Cantidad de stock es obligatoria').min(0,'Debe ser ≥ 0'),
    }),
    onSubmit: async (datos) => {
      if (subiendo) return;

      // Progreso simulado hasta 90%
      const startProgress = () => {
        setProgreso(0);
        tickRef.current = setInterval(() => {
          setProgreso(prev => {
            const next = typeof prev === 'number' ? prev + Math.random()*8 : 0;
            return Math.min(90, Math.round(next));
          });
        }, 120);
      };
      const stopProgress = () => {
        if (tickRef.current) clearInterval(tickRef.current);
        tickRef.current = null;
      };

      // Abort por timeout
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort('timeout'), 20000); // 20s

      try {
        setSubiendo(true);
        startProgress();

        const fd = new FormData();
        fd.append('nombre', datos.nombre);
        fd.append('marca', datos.marca);
        fd.append('presentacion', datos.presentacion);
        fd.append('precio', String(datos.precio));
        fd.append('stock',  String(datos.stock));
        if (imagenFile) fd.append('imagen', imagenFile);

        const resp = await fetch(`${API_BASE}/api/productos`, {
          method: 'POST',
          body: fd,
          credentials: 'include',
          signal: controller.signal,
        });

        stopProgress();
        setProgreso(100);

        let data = null;
        try { data = await resp.json(); } catch {}

        if (!resp.ok || data?.ok === false) {
          const msg = data?.msg || data?.error || `Error ${resp.status}`;
          throw new Error(msg);
        }

        setSubiendo(false);
        alert('✅ Producto guardado con éxito');
        navigate(LIST_ROUTE, { replace: true });
      } catch (e) {
        stopProgress();
        clearTimeout(timeout);

        // --- Fallback inteligente ante timeout: verificamos si el producto quedó en BD
        const fueTimeout = e?.name === 'AbortError' || String(e?.message || e).toLowerCase().includes('timeout');
        if (fueTimeout) {
          try {
            const r = await fetch(`${API_BASE}/api/productos?t=${Date.now()}`, { credentials:'include', cache:'no-store' });
            const lista = (await r.json()) || [];
            const match = Array.isArray(lista) && lista.find(p =>
              String(p?.nombre||'').trim().toLowerCase() === datos.nombre.trim().toLowerCase() &&
              String(p?.marca||'').trim().toLowerCase() === datos.marca.trim().toLowerCase() &&
              String(p?.presentacion||'').trim().toLowerCase() === datos.presentacion.trim().toLowerCase()
            );
            if (match) {
              setProgreso(100);
              setSubiendo(false);
              alert('✅ Producto guardado (confirmado tras timeout)');
              return navigate(LIST_ROUTE, { replace: true });
            }
          } catch { /* si falla la verificación, seguimos al catch normal */ }
        }

        // Si no hubo match o no fue timeout:
        setSubiendo(false);
        setProgreso(null);
        console.error('Error al guardar producto:', e);
        alert(fueTimeout ? 'La solicitud tardó demasiado y fue cancelada. Intenta de nuevo.' : String(e.message || e));
      } finally {
        clearTimeout(timeout);
        // Limpiar progreso inmediatamente sin demoras cosméticas
        setProgreso(null);
      }
    },
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.includes('image')) return alert('Solo se permiten imágenes');
    setImagenFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // previewSrc removed (no placeholder image to avoid visual blinking)

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800">Nuevo producto</h1>
          <p className="text-slate-500">Completa los campos y sube una imagen (opcional).</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(LIST_ROUTE)}
          className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
          disabled={subiendo}
        >
          ← Volver
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow p-6 sm:p-8">
        <form onSubmit={formik.handleSubmit} encType="multipart/form-data" className="grid md:grid-cols-5 gap-6">
          {/* Imagen */}
          <div className="md:col-span-2">
            <div className="rounded-2xl border p-3">
              <div className="aspect-square rounded-xl overflow-hidden border bg-white flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = NODISP; }}
                  />
                ) : (
                  <div className="text-sm text-slate-500">Sin imagen seleccionada</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label
                  htmlFor="imagen"
                  className={`inline-flex items-center px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm ${subiendo ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  Seleccionar imagen
                  <input id="imagen" type="file" accept="image/*" onChange={onFile} className="hidden" disabled={subiendo} />
                </label>
                <span className="text-xs text-slate-500 truncate">
                  {imagenFile?.name || 'Sin archivo seleccionado'}
                </span>
              </div>

              {progreso !== null && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-slate-600">{progreso >= 100 ? 'Completado' : 'Subiendo imagen…'}</span>
                    <span className="text-slate-500">{progreso}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${progreso >= 100 ? 'bg-emerald-500' : 'bg-sky-500'}`}
                      style={{ width: `${progreso}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Formulario */}
          <div className="md:col-span-3">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre" name="nombre" formik={formik} disabled={subiendo} />
              <Field label="Marca" name="marca" formik={formik} disabled={subiendo} />
              <Field label="Presentación" name="presentacion" formik={formik} disabled={subiendo} full />
              <Field label="Precio" name="precio" type="number" step="0.01" formik={formik} disabled={subiendo} />
              <Field label="Stock" name="stock" type="number" formik={formik} disabled={subiendo} />
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={subiendo}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60"
              >
                {subiendo ? 'Guardando…' : 'Guardar producto'}
              </button>
              <button
                type="button"
                onClick={() => navigate(LIST_ROUTE)}
                className="px-5 py-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold"
                disabled={subiendo}
              >
                Cancelar
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-3">
              Si no adjuntas imagen, el backend usará <code>nodisponible.png</code> automáticamente.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, formik, type='text', step, disabled, full=false }) {
  const err = formik.touched[name] && formik.errors[name];
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        step={step}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={`mt-1 w-full rounded-lg border px-3 py-2 ${err ? 'border-rose-400' : 'border-slate-300'}`}
        disabled={disabled}
      />
      {err && <p className="text-rose-600 text-sm mt-1">{formik.errors[name]}</p>}
    </div>
  );
}
